/* "Not saved" (owner decision 2026-10-13: never lose a time record). A change the database refuses (not a network
   problem: those are retried) is never thrown away: it moves from the queue of changes to this list, kept on the device
   per login (localStorage, also across sign-out), until the person tries again, fixes it, or discards it after a
   confirm. The pure part (no network, no DOM; tests/app/notsaved.test.ts). */
import type {Entry, Timer} from './model.ts';
import type {Op} from './sync.ts';
import {friendlyDbError} from './people.ts';

export interface NotSaved {
  id: string;                  // the operation's id
  op: Op;                      // exactly what was refused (Try again sends it again)
  reason: string;              // the database's own words
  at: string;                  // when it was refused (ISO)
  server?: Entry | Timer | null;   // for a change / delete / timer change: what the server has now (null: nothing there; undefined: not known)
  names?: {project: string; client: string};   // the project's names when it was refused (after an unassignment a member can no longer see it)
  waiting?: true;              // not refused: still on its way when the person signed out (sent again automatically at their next sign-in)
}

/* Sign-out with changes still waiting to be sent (owner decision 2026-10-13: never lose a time record): they are parked in
   this person's own Not saved list (per login, kept across sign-out) and, when the same login signs in again, put back in
   the queue in the same order and sent. Another person signing in on the device never reads them (a different key). */
export const SIGNED_OUT_REASON = 'Signed out before this reached the server.';
/** park the queue (in order) in the Not saved list; an item with the same id is replaced */
export function parkOps(list: NotSaved[], ops: Op[], at = new Date().toISOString()): NotSaved[] {
  const ids = new Set(ops.map(o => o.opId));
  return [...list.filter(x => !ids.has(x.id)), ...ops.map(op => ({id: op.opId, op, reason: SIGNED_OUT_REASON, at, waiting: true as const, ...(op.names ? {names: op.names} : {})}))];
}
/** at sign-in: the parked changes (in their original order) to queue again, and what stays in the list */
export function unparkOps(list: NotSaved[]): {ops: Op[]; rest: NotSaved[]} {
  return {ops: list.filter(x => x.waiting).map(x => x.op), rest: list.filter(x => !x.waiting)};
}

/** the database's refusal in plain words, with what the person can do about it */
export function reasonText(raw: string): string {
  const m = String(raw || '');
  if (m === SIGNED_OUT_REASON) return 'You signed out before this reached the server. It is sent again when you are signed in and online — or Try again now.';
  if (/not assigned to this project/i.test(m)) return 'You are not assigned to this project (any more). Ask the owner to add you to it, then Try again — or Edit and save it on another project.';
  if (/project is archived/i.test(m)) return 'This project is archived. Ask the owner to restore it, then Try again — or Edit and save it on another project.';
  if (/not a real Chicago time/i.test(m)) return 'The start time is not a real Central time (the clocks skip or repeat an hour on daylight-saving days). Edit the time and save.';
  if (/row-level security|permission denied|cannot change|your role/i.test(m)) return 'Your role does not allow this change (it may have changed since). Ask the owner, then Try again.';
  if (/already running/i.test(m)) return 'A timer was already running for this person (started on another device).';
  if (/stopped or changed on another device|deleted on another device/i.test(m)) return m;
  return friendlyDbError(m) || m || 'The database refused it.';
}

/** what kind of change it was, in a few words */
export function itemTitle(op: Op): string {
  switch (op.kind) {
    case 'saveEntry': return op.isNew ? 'New entry' : 'Change to an entry';
    case 'deleteEntry': return 'Deleting an entry';
    case 'startTimer': return 'Starting a timer';
    case 'updateTimer': return 'Change to a running timer';
    case 'stopTimer': return 'Stopped timer (its time)';
  }
}
/** the entry the change would have saved (none for deletes and timer starts / changes) */
export function opEntry(op: Op): Entry | null { return op.kind === 'saveEntry' || op.kind === 'stopTimer' ? op.entry : null; }
/** the timer a timer change is about */
export function opTimer(op: Op): Timer | null { return op.kind === 'startTimer' || op.kind === 'updateTimer' ? op.timer : op.kind === 'stopTimer' ? op.timer : null; }
/** seconds of time that discarding would throw away (a change or a delete throws away none: the server keeps its version) */
export function secondsAtStake(op: Op, nowMs: number): number {
  if (op.kind === 'saveEntry') return op.isNew ? op.entry.durationSeconds : 0;
  if (op.kind === 'stopTimer') return op.entry.durationSeconds;
  if (op.kind === 'startTimer') return Math.max(0, Math.round((nowMs - Date.parse(op.timer.startedAt)) / 1000));
  return 0;
}
/** which actions make sense: every item can be tried again, copied and discarded; Edit and save where there is an entry to fix */
export function canEdit(op: Op): boolean { return op.kind === 'saveEntry' || op.kind === 'stopTimer' || op.kind === 'startTimer'; }

/** add a refused change (an item with the same id replaces the old one) */
export function addItem(list: NotSaved[], op: Op, reason: string, at = new Date().toISOString(), names?: NotSaved['names']): NotSaved[] {
  return [...list.filter(x => x.id !== op.opId), {id: op.opId, op, reason, at, ...(names ? {names} : {})}];   // (a refusal: no longer 'waiting')
}
export const removeItem = (list: NotSaved[], id: string) => list.filter(x => x.id !== id);

/** Try again: the same change, sent again. A running timer's change is made against the timer as it is now (it may have
    been changed since); with no such timer running any more it is sent as it was (and refused again, kept). */
export function retryOp(item: NotSaved, timers: Timer[]): Op {
  const op = item.op;
  if (op.kind === 'updateTimer') {
    const cur = timers.find(t => t.memberId === op.prev.memberId);
    if (cur) return {...op, prev: cur};
  }
  return op;
}
